import { useState, useRef, useEffect, useCallback } from 'react';
import imageCompression from 'browser-image-compression';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import heic2any from 'heic2any';
import { toast } from 'sonner';

export function formatBytes(bytes, allowNegative = false) {
  if (bytes === 0) return "0 Bytes";
  const isNegative = bytes < 0;
  const absBytes = Math.abs(bytes);
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB"];
  const i = Math.floor(Math.log(absBytes) / Math.log(k));
  const formatted = Math.round((absBytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  return allowNegative && isNegative ? "+" + formatted : formatted;
}

export function useImageCompression() {
  const [images, setImages] = useState([]);
  const [quality, setQuality] = useState([85]);
  const [format, setFormat] = useState("webp");
  const [maxWidth, setMaxWidth] = useState([0]);
  const [maxHeight, setMaxHeight] = useState([0]);
  const [compressionPreset, setCompressionPreset] = useState("balanced");
  const [targetPercentage, setTargetPercentage] = useState([50]);
  const [usePercentageMode, setUsePercentageMode] = useState(false);
  const [removeExif, setRemoveExif] = useState(false);
  const [exifOptions, setExifOptions] = useState({
    camera: true,
    gps: true,
    device: true
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingImageId, setProcessingImageId] = useState(null);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [comparisonMode, setComparisonMode] = useState(false);

  const fileInputRef = useRef(null);
  const addMoreInputRef = useRef(null);
  const updateTimerRef = useRef({});
  const globalUpdateTimerRef = useRef(null);

  // Helper function to convert HEIC to JPEG
  const convertHeicToJpeg = async (file) => {
    const fileName = file.name.toLowerCase();
    if (fileName.endsWith('.heic') || fileName.endsWith('.heif') || file.type === 'image/heic' || file.type === 'image/heif') {
      try {
        toast.info('Converting HEIC format...', { duration: 2000 });
        const convertedBlob = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.92
        });
        const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
        const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
        return new File([blob], newName, { type: 'image/jpeg' });
      } catch (error) {
        console.error('HEIC conversion error:', error);
        toast.error('Failed to convert HEIC image');
        throw error;
      }
    }
    return file;
  };

  // Advanced compression using binary search for optimal quality
  const compressImage = async (file, settings) => {
    try {
      const originalSizeMB = file.size / 1024 / 1024;
      const originalSizeKB = file.size / 1024;
      
      let targetSizeKB;
      let maxDimension;
      
      if (settings.targetSize) {
        targetSizeKB = settings.targetSize;
        maxDimension = 2560;
      } else if (settings.targetPercentage) {
        targetSizeKB = (originalSizeKB * settings.targetPercentage) / 100;
        maxDimension = undefined;
      } else if (settings.preset === "maximum") {
        targetSizeKB = Math.max(30, originalSizeKB * 0.25);
        maxDimension = 1920;
      } else if (settings.preset === "balanced") {
        targetSizeKB = Math.max(50, originalSizeKB * 0.45);
        maxDimension = 2560;
      } else {
        targetSizeKB = Math.max(100, originalSizeKB * 0.8);
        maxDimension = 4096;
      }
      
      const userMaxDim = settings.maxWidth > 0 || settings.maxHeight > 0 
        ? Math.max(settings.maxWidth || 0, settings.maxHeight || 0) 
        : undefined;
      const finalMaxDimension = userMaxDim || maxDimension;
      
      let fileType = "image/jpeg";
      if (settings.format === "png") {
        fileType = "image/png";
      } else if (settings.format === "webp") {
        fileType = "image/webp";
      } else if (settings.format === "gif") {
        fileType = "image/gif";
      }
      
      // Binary search for optimal quality
      const findOptimalQuality = async () => {
        let minQuality = 0.1;
        let maxQuality = settings.quality / 100;
        let bestResult = null;
        let bestQuality = minQuality;
        
        const options = {
          maxSizeMB: targetSizeKB / 1024,
          maxWidthOrHeight: finalMaxDimension,
          useWebWorker: true,
          initialQuality: maxQuality,
          maxIteration: 30,
          preserveExif: !settings.removeExif,
          fileType: fileType,
          alwaysKeepResolution: !finalMaxDimension,
        };
        
        let result = await imageCompression(file, options);
        let resultSizeKB = result.size / 1024;
        
        if (resultSizeKB <= targetSizeKB) {
          return result;
        }
        
        // Binary search for optimal quality
        for (let i = 0; i < 8; i++) {
          const midQuality = (minQuality + maxQuality) / 2;
          
          const testOptions = {
            ...options,
            initialQuality: midQuality,
            maxSizeMB: targetSizeKB / 1024,
          };
          
          result = await imageCompression(file, testOptions);
          resultSizeKB = result.size / 1024;
          
          if (resultSizeKB <= targetSizeKB) {
            bestResult = result;
            bestQuality = midQuality;
            minQuality = midQuality;
          } else {
            maxQuality = midQuality;
          }
        }
        
        return bestResult || result;
      };
      
      const compressedFile = await findOptimalQuality();
      const url = URL.createObjectURL(compressedFile);
      
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          resolve({
            src: url,
            blob: compressedFile,
            size: compressedFile.size,
            width: img.width,
            height: img.height,
            quality: settings.quality,
          });
        };
        img.onerror = () => {
          resolve({
            src: url,
            blob: compressedFile,
            size: compressedFile.size,
            width: 0,
            height: 0,
          });
        };
        img.src = url;
      });
    } catch (error) {
      console.error("Compression error:", error);
      return null;
    }
  };

  const compressWithAllPresets = async (file, settings) => {
    const presets = ["quality", "balanced", "maximum"];
    const results = {};
    
    for (const preset of presets) {
      const presetSettings = { ...settings, preset };
      results[preset] = await compressImage(file, presetSettings);
    }
    
    return results;
  };

  const processImage = async (file) => {
    const originalFileName = file.name.toLowerCase();
    const wasHeic = originalFileName.endsWith('.heic') || originalFileName.endsWith('.heif') || 
                    file.type === 'image/heic' || file.type === 'image/heif';
    const isSvg = originalFileName.endsWith('.svg') || file.type === 'image/svg+xml';
    const isGif = originalFileName.endsWith('.gif') || file.type === 'image/gif';
    const originalFormat = wasHeic ? 'HEIC' : isSvg ? 'SVG' : isGif ? 'GIF' : file.type.split('/')[1]?.toUpperCase() || 'Unknown';
    
    // SVG files - pass through unchanged
    if (isSvg) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve({
            id: Math.random().toString(36).substr(2, 9),
            name: file.name,
            originalSrc: e.target.result,
            originalSize: file.size,
            originalWidth: 0,
            originalHeight: 0,
            originalFormat: 'SVG',
            wasConverted: false,
            isSvg: true,
            file: file,
            compressed: {
              src: e.target.result,
              blob: file,
              size: file.size,
              width: 0,
              height: 0,
            },
            comparisonData: null,
            settings: {
              quality: quality[0],
              format: 'svg',
              maxWidth: maxWidth[0],
              maxHeight: maxHeight[0],
              preset: compressionPreset,
              removeExif,
              exifOptions: { ...exifOptions }
            }
          });
        };
        reader.readAsDataURL(file);
      });
    }
    
    // GIF files - compress but preserve as GIF
    if (isGif) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
          const img = new Image();
          img.onload = async () => {
            const imageData = {
              id: Math.random().toString(36).substr(2, 9),
              name: file.name,
              originalSrc: e.target.result,
              originalSize: file.size,
              originalWidth: img.width,
              originalHeight: img.height,
              originalFormat: 'GIF',
              wasConverted: false,
              isGif: true,
              file: file,
              compressed: null,
              comparisonData: null,
              settings: {
                quality: quality[0],
                format: 'gif',
                maxWidth: maxWidth[0],
                maxHeight: maxHeight[0],
                preset: compressionPreset,
                removeExif,
                exifOptions: { ...exifOptions }
              }
            };
            
            const compressed = await compressImage(file, { ...imageData.settings, format: 'gif' });
            imageData.compressed = compressed;
            
            resolve(imageData);
          };
          img.src = e.target.result;
        };
        reader.readAsDataURL(file);
      });
    }
    
    // Convert HEIC to JPEG first if needed
    const processableFile = await convertHeicToJpeg(file);
    
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = async () => {
          const imageData = {
            id: Math.random().toString(36).substr(2, 9),
            name: processableFile.name,
            originalSrc: e.target.result,
            originalSize: file.size,
            originalWidth: img.width,
            originalHeight: img.height,
            originalFormat: originalFormat,
            wasConverted: wasHeic,
            file: processableFile,
            compressed: null,
            comparisonData: null,
            settings: {
              quality: quality[0],
              format: format,
              maxWidth: maxWidth[0],
              maxHeight: maxHeight[0],
              preset: compressionPreset,
              removeExif: removeExif,
              exifOptions: { ...exifOptions }
            }
          };
          
          if (comparisonMode) {
            const comparisonData = await compressWithAllPresets(processableFile, imageData.settings);
            imageData.comparisonData = comparisonData;
          } else {
            const compressed = await compressImage(processableFile, imageData.settings);
            imageData.compressed = compressed;
          }
          
          resolve(imageData);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(processableFile);
    });
  };

  const processMultipleImages = async (files) => {
    setIsProcessing(true);
    setCompressionProgress(0);
    
    const imagePromises = files.map((file, index) => 
      processImage(file).then(result => {
        setCompressionProgress(Math.round(((index + 1) / files.length) * 100));
        return result;
      })
    );
    
    const processedImages = await Promise.all(imagePromises);
    setImages(prevImages => [...prevImages, ...processedImages]);
    
    setIsProcessing(false);
    setCompressionProgress(0);
    toast.success(`${files.length} image${files.length > 1 ? 's' : ''} added!`);
  };

  const updateImageSettings = async (imageId, newSettings) => {
    const imageIndex = images.findIndex(img => img.id === imageId);
    if (imageIndex === -1) return;
    
    if (updateTimerRef.current[imageId]) {
      clearTimeout(updateTimerRef.current[imageId]);
    }
    
    setImages(prevImages => {
      const updated = [...prevImages];
      updated[imageIndex] = {
        ...updated[imageIndex],
        settings: { ...updated[imageIndex].settings, ...newSettings }
      };
      return updated;
    });
    
    updateTimerRef.current[imageId] = setTimeout(async () => {
      setProcessingImageId(imageId);
      
      try {
        const currentImage = images[imageIndex];
        const updatedSettings = { ...currentImage.settings, ...newSettings };
        
        let compressed;
        let comparisonData;
        
        if (comparisonMode) {
          comparisonData = await compressWithAllPresets(currentImage.file, updatedSettings);
        } else {
          compressed = await compressImage(currentImage.file, updatedSettings);
        }
        
        setImages(prevImages => {
          const finalImages = [...prevImages];
          const finalIndex = finalImages.findIndex(img => img.id === imageId);
          
          if (finalIndex !== -1) {
            finalImages[finalIndex] = {
              ...finalImages[finalIndex],
              settings: updatedSettings,
              compressed: compressed || finalImages[finalIndex].compressed,
              comparisonData: comparisonData || finalImages[finalIndex].comparisonData
            };
          }
          
          return finalImages;
        });
      } catch (error) {
        console.error("Error recompressing image:", error);
      } finally {
        setProcessingImageId(null);
      }
    }, 800);
  };

  const handleDownloadSingle = (image) => {
    if (!image.compressed) return;
    
    const link = document.createElement("a");
    link.href = image.compressed.src;
    const extension = format === "png" ? "png" : format === "webp" ? "webp" : "jpg";
    const nameWithoutExt = image.name.replace(/\.[^/.]+$/, "");
    link.download = `${nameWithoutExt}_compressed.${extension}`;
    link.click();
    toast.success("Download started!");
  };

  const handleDownloadAll = async () => {
    if (images.length === 0) return;
    
    const zip = new JSZip();
    const extension = format === "png" ? "png" : format === "webp" ? "webp" : "jpg";
    
    for (const image of images) {
      if (image.compressed) {
        const nameWithoutExt = image.name.replace(/\.[^/.]+$/, "");
        zip.file(`${nameWithoutExt}_compressed.${extension}`, image.compressed.blob);
      }
    }
    
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, `compressed_images_${Date.now()}.zip`);
    toast.success("All images downloaded!");
  };

  const handleReset = () => {
    setImages([]);
    setComparisonMode(false);
    toast.info("Ready for new images");
  };

  const handleClearAll = () => {
    if (window.confirm("Are you sure you want to clear all images?")) {
      handleReset();
    }
  };

  const handleRemoveImage = (imageId) => {
    const updatedImages = images.filter(img => img.id !== imageId);
    setImages(updatedImages);
    
    if (updatedImages.length === 0) {
      setComparisonMode(false);
    }
    
    toast.success("Image removed");
  };

  const handleQuickCompress = async (targetSizeKB, imageId = null) => {
    if (images.length === 0) return;
    
    if (imageId) {
      setProcessingImageId(imageId);
      toast.info(`Compressing to ${targetSizeKB}KB...`);
    } else {
      setIsProcessing(true);
      setCompressionProgress(0);
      toast.info(`Compressing all to ${targetSizeKB}KB...`);
    }
    
    try {
      const totalImages = images.length;
      let completed = 0;
      
      const updatedImages = await Promise.all(
        images.map(async (img) => {
          if (imageId && img.id !== imageId) {
            return img;
          }
          
          const targetSettings = {
            quality: img.settings.quality,
            format: img.settings.format,
            maxWidth: img.settings.maxWidth,
            maxHeight: img.settings.maxHeight,
            preset: img.settings.preset,
            removeExif: img.settings.removeExif,
            exifOptions: img.settings.exifOptions,
            targetSize: targetSizeKB
          };
          
          const compressed = await compressImage(img.file, targetSettings);
          
          completed++;
          if (!imageId) {
            setCompressionProgress(Math.round((completed / totalImages) * 100));
          }
          
          return {
            ...img,
            compressed: compressed || img.compressed,
            settings: targetSettings
          };
        })
      );
      
      setImages(updatedImages);
      const successMessage = imageId ? `Image compressed to ~${targetSizeKB}KB!` : `All images compressed to ~${targetSizeKB}KB!`;
      toast.success(successMessage);
    } catch (error) {
      console.error("Quick compress error:", error);
      toast.error("Compression failed");
    } finally {
      if (imageId) {
        setProcessingImageId(null);
      } else {
        setIsProcessing(false);
        setCompressionProgress(0);
      }
    }
  };

  const handlePercentageCompress = async (percentage, imageId = null) => {
    if (images.length === 0) return;
    
    try {
      const imagesToProcess = imageId 
        ? images.filter(img => img.id === imageId)
        : images;
      
      if (imagesToProcess.length === 0) return;
      
      if (imageId) {
        setProcessingImageId(imageId);
      } else {
        setIsProcessing(true);
        setCompressionProgress(0);
      }
      
      let completed = 0;
      const totalImages = imagesToProcess.length;
      
      const updatedImages = await Promise.all(
        images.map(async (img) => {
          if (imageId && img.id !== imageId) {
            return img;
          }
          
          const targetSettings = {
            ...img.settings,
            targetPercentage: percentage
          };
          
          const compressed = await compressImage(img.file, targetSettings);
          
          completed++;
          if (!imageId) {
            setCompressionProgress(Math.round((completed / totalImages) * 100));
          }
          
          return {
            ...img,
            compressed: compressed || img.compressed,
            settings: targetSettings
          };
        })
      );
      
      setImages(updatedImages);
      toast.success(`Compressed to ${percentage}% of original size!`);
    } catch (error) {
      console.error("Percentage compress error:", error);
      toast.error("Compression failed");
    } finally {
      if (imageId) {
        setProcessingImageId(null);
      } else {
        setIsProcessing(false);
        setCompressionProgress(0);
      }
    }
  };

  const toggleComparisonMode = async () => {
    const newMode = !comparisonMode;
    setComparisonMode(newMode);
    
    if (images.length > 0) {
      setIsProcessing(true);
      const updatedImages = await Promise.all(
        images.map(async (img) => {
          if (newMode) {
            const comparisonData = await compressWithAllPresets(img.file, img.settings);
            return { ...img, comparisonData };
          } else {
            const compressed = await compressImage(img.file, img.settings);
            return { ...img, compressed };
          }
        })
      );
      setImages(updatedImages);
      setIsProcessing(false);
    }
  };

  // Calculate stats
  const calculateTotalStats = useCallback(() => {
    let totalOriginal = 0;
    let totalCompressed = 0;
    
    images.forEach(img => {
      totalOriginal += img.originalSize;
      if (img.compressed) {
        totalCompressed += img.compressed.size;
      }
    });
    
    const savings = totalOriginal - totalCompressed;
    const percentage = totalOriginal > 0 ? Math.round((savings / totalOriginal) * 100) : 0;
    
    return { totalOriginal, totalCompressed, savings, percentage };
  }, [images]);

  // Auto-update all images when global settings change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (images.length > 0 && !isProcessing) {
      if (globalUpdateTimerRef.current) {
        clearTimeout(globalUpdateTimerRef.current);
      }
      
      globalUpdateTimerRef.current = setTimeout(async () => {
        setIsProcessing(true);
        
        try {
          const updatedImages = await Promise.all(
            images.map(async (img) => {
              const updatedSettings = {
                quality: quality[0],
                format: format,
                maxWidth: maxWidth[0],
                maxHeight: maxHeight[0],
                preset: compressionPreset,
                removeExif: removeExif,
                exifOptions: { ...exifOptions }
              };
              
              let compressed;
              let comparisonData;
              
              if (comparisonMode) {
                comparisonData = await compressWithAllPresets(img.file, updatedSettings);
              } else {
                compressed = await compressImage(img.file, updatedSettings);
              }
              
              return {
                ...img,
                settings: updatedSettings,
                compressed: compressed || img.compressed,
                comparisonData: comparisonData || img.comparisonData
              };
            })
          );
          
          setImages(updatedImages);
        } catch (error) {
          console.error("Error recompressing images:", error);
        } finally {
          setIsProcessing(false);
        }
      }, 800);
    }
  }, [quality, format, maxWidth, maxHeight, compressionPreset, comparisonMode, removeExif]);

  return {
    // State
    images,
    quality,
    format,
    maxWidth,
    maxHeight,
    compressionPreset,
    targetPercentage,
    usePercentageMode,
    removeExif,
    exifOptions,
    isDragging,
    isProcessing,
    processingImageId,
    compressionProgress,
    comparisonMode,
    
    // Refs
    fileInputRef,
    addMoreInputRef,
    
    // Setters
    setImages,
    setQuality,
    setFormat,
    setMaxWidth,
    setMaxHeight,
    setCompressionPreset,
    setTargetPercentage,
    setUsePercentageMode,
    setRemoveExif,
    setExifOptions,
    setIsDragging,
    setComparisonMode,
    
    // Actions
    processMultipleImages,
    updateImageSettings,
    handleDownloadSingle,
    handleDownloadAll,
    handleReset,
    handleClearAll,
    handleRemoveImage,
    handleQuickCompress,
    handlePercentageCompress,
    toggleComparisonMode,
    calculateTotalStats,
    compressImage,
    compressWithAllPresets,
  };
}
